import { createSelector } from 'reselect';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmDataPlanId,
  getKnmErknmKnoOrganizationDictVersionIdFormValue,
  getKnmErknmSupervisionTypeDictVersionIdFormValue,
  getKnmIsTypeErknmPlanned,
  getKnmKnoSupervisionUuid,
  getKnmPmIsUnableToRefuseFormValue,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IFederalLawErvk } from 'APIPackage/CatalogsModule/interfaces/IFederalLawErvk';
import { KnmSubjectTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmSubjectTypeCodesEnum';
import { FederalLawCodesEnum } from 'APIPackage/CatalogsModule/enums/FederalLawCodesEnum';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import { createDictionaryKnoOrganizationSelectOption } from '../common/createDictionaryKnoOrganizationSelectOption';
import { createDictionaryPermitDocsSelectOption } from '../common/createDictionaryPermitDocsSelectOption';
import { createDictionaryPositionSelectOption } from '../common/createDictionaryPositionSelectOption';
import { createDictionaryKnmKindSelectOption } from '../common/createDictionaryKnmKindSelectOption';
import { createDictionaryProfKindSelectOption } from '../common/createDictionaryProfKindSelectOption';
import { createDictionaryRiskCategorySelectOption } from '../common/createDictionaryRiskCategorySelectOption';
import { createDictionaryDangerClassSelectOption } from '../common/createDictionaryDangerClassSelectOption';
import { createDictionarySubjectTypeSelectOption } from '../common/createDictionarySubjectTypeSelectOption';
import { createDictionarySubjectKindSelectOption } from '../common/createDictionarySubjectKindSelectOption';
import { createDictionarySubjectSubKindSelectOption } from '../common/createDictionarySubjectSubKindSelectOption';
import { createDictionaryEventTypeSelectOption } from '../common/createDictionaryEventTypeSelectOption';
import {
  createDictionarySupervisionTypeExtendedSelectOption,
  createDictionarySupervisionTypeSelectOption,
} from '../common/createDictionarySupervisionTypeSelectOption';
import { createDictionaryAcceptedDocumentSelectOption } from '../common/createDictionaryAcceptedDocumentSelectOption';
import { createDictionaryChecklistSelectOption } from '../common/createDictionaryChecklistSelectOption';
import { createDictionaryRiskIndicatorySelectOption } from '../common/createDictionaryRiskIndicatorySelectOption';
import { createDictionaryRequirementSelectOption } from '../common/createDictionaryRequirementSelectOption';
import { getDictionaryConstraintUuidKey } from '../common/getDictionaryConstraintUuidKey';
import { getDictionaryKey } from '../common/getDictionaryKey';
import { getDictionariesItemValueKey } from '../common/getDictionariesItemValueKey';
import { createDictionarySelectOptionByNameMap } from '../constants/createDictionarySelectOptionByNameMap';
import { dictionaryNameById } from '../constants/dictionaryNameById';
import { DictionaryNamesEnum } from '../enums/DictionaryNamesEnum';
import { DictionaryUUIDsEnum } from '../enums/DictionaryUUIDsEnum';
import { createDictionaryParticipantPositionSelectOption } from '../common/createDictionaryParticipantPositionSelectOption';
import { IPairKnoSupervisionVersions } from '../interfaces/IPairKnoSupervisionVersions';
import { createDictionaryTargetEntitySelectOption } from '../common/createDictionaryTargetEntitySelectOption';
import { IDictionaryValue } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryValue';
import { IPermitDocs } from '../../../APIPackage/DictionariesModule/interfaces/IPermitDocs';
import { createErvkChecklistSelectOption } from '../common/createErvkChecklistSelectOption';
import { IErvkCheckList } from '../../../APIPackage/DictionariesModule/interfaces/IErvkCheckList';
import { ITripletKnoSupervisionCondition } from '../interfaces/ITripletKnoSupervisionCondition';
import { createDictionaryDecisionKindSelectOption } from '../common/createDictionaryDecisionKindSelectOption';
import { createDictionaryRiskIndicatorySelectOptionForDecision } from '../common/createDictionaryRiskIndicatorySelectOptionForDecision';
import { IDictionarySelectOption } from '../interfaces/IDictionarySelectOption';
import { ISupervisionType } from '../../../APIPackage/DictionariesModule/interfaces/ISupervisionType';
import { IDecisionKind } from '../../../APIPackage/DictionariesModule/interfaces/IDecisionKind';
import { createDictionaryErvkSelectOption } from '../common/createDictionaryErvkSelectOption';
import { createDictionaryKnmSubjectTypesSelectOption } from '../common/createDictionaryKnmSubjectTypesSelectOption';
import { IKnmErvk } from '../../../APIPackage/CatalogsModule/interfaces/IKnmErvk';
import { ISubjectType } from '../../../APIPackage/DictionariesModule/interfaces/ISubjectType';
import { IRiskCategory248 } from '../../../APIPackage/DictionariesModule/interfaces/IRiskCategory248';
import { IEventType } from '../../../APIPackage/DictionariesModule/interfaces/IEventType';
import { createDictionaryFederalLawSelectOption } from '../common/createDictionaryFederalLawSelectOption';
import { hasDictionaryFailedDeep } from '../common/hasDictionaryFailedDeep';
import { getLawInterface } from '../../MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../MainModule/enums/LawInterfacesEnum';
import { availableFederalLaws } from '../constants/availableFederalLaws';
import { IPlanTypeWithFederalLaw } from '../../../ImportPackage/MainModule/interfaces/IPlanTypeWithFederalLaw';
import { getKnmPlanTypeSelectOptions } from '../../CatalogsModule/selectors/catalogsSelectors';
import { IKnmPlanTypeOption } from '../../CatalogsModule/interfaces/IKnmPlanTypeOption';

interface IGetDictionariesChecklistsParams {
  uuidKno: string;
  uuidSupervision: string;
}

interface IGetDictionariesRequirementsParams {
  uuidKno: string;
  uuidSupervision: string;
}

interface IGetErvkCheckListParams {
  controlKindId: string;
  knmKindId: string;
}

interface IFilteredFederalLawsParams {
  isPm?: boolean;
}

export const getDictionariesState = (state: IRootState) => state.dictionaries;

// -- KnoSupervision all data --
export const getDictionariesKnoSupervision = (state: IRootState, knoVersionId: string, supervisionVersionId: string) =>
  state.dictionaries.knoSupervision[getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId])];
export const getDictionariesKnoSupervisionData = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
) => getDictionariesKnoSupervision(state, knoVersionId, supervisionVersionId)?.data;
export const getDictionariesKnoSupervisionUuid = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
) => getDictionariesKnoSupervisionData(state, knoVersionId, supervisionVersionId)?.generatedUuid;
export const getDictionariesKnoSupervisionHazardClass = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
) => getDictionariesKnoSupervisionData(state, knoVersionId, supervisionVersionId)?.hazardClassApplies;
export const getDictionariesKnoSupervisionParentKnoUuid = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
) => getDictionariesKnoSupervisionData(state, knoVersionId, supervisionVersionId)?.parentKnoUuid;
export const getKnoSupervisionHazardClassApplies = (state: IRootState) => {
  const knoVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoVersionId && supervisionVersionId) {
    return getDictionariesKnoSupervisionHazardClass(state, knoVersionId, supervisionVersionId);
  }
};

export const getFederalLawsStatus = (state: IRootState) => state.dictionaries.federalLawsStatus;

export const getFederalLawsData = (state: IRootState) => state.dictionaries.federalLaws;

export const getDictionariesFederalLawsSelectOptions = createSelector(getFederalLawsData, dictionaryData =>
  dictionaryData?.values.map(item => createDictionaryFederalLawSelectOption(dictionaryData.idDictionary, item)),
);

export const getFilteredFederalLawsData = (state: IRootState) => state.dictionaries.filteredFederalLaws;

export const makeGetFilteredFederalLawsSelectOptions = (params?: IFilteredFederalLawsParams) => {
  return createSelector(
    [getFilteredFederalLawsData, getFederalLawsData, getLawInterface],
    (filteredLaws, allLaws, lawInterface) => {
      const isErknm = lawInterface === LawInterfacesEnum.FZ_248;
      const isErp = !isErknm;
      const isPm = params?.isPm;

      // Базовая фильтрация по существующей логике
      let lawsToProcess = allLaws?.values || [];

      if (filteredLaws && filteredLaws.values.length > 0) {
        const filteredLawIds = filteredLaws.values.map((law: IDictionaryValue<IFederalLawErvk>) => law.value.id);

        lawsToProcess = lawsToProcess.filter(item => filteredLawIds.includes(item.value.id));
      }

      /**
       * В результате обсуждений в TASK-5511 пришли к тому что необходима
       * дополнительная фронтовая фильтрация ФЗ
       *
       * Дополнительная фильтрация по availableFederalLaws, если переданы параметры
       */
      if ((isPm !== undefined || isErknm !== undefined || isErp !== undefined) && lawsToProcess.length > 0) {
        lawsToProcess = lawsToProcess.filter(item => {
          const lawNumber = item.value.number;
          const accessibility = availableFederalLaws[lawNumber as FederalLawCodesEnum];

          if (!accessibility) return false;

          return (
            (isErknm === undefined ||
              accessibility.isErknm === isErknm ||
              isErp === undefined ||
              accessibility.isErp === isErp) &&
            (isPm === undefined || (isPm ? accessibility.isPm !== false : accessibility.isPm !== true))
          );
        });
      }

      if (lawsToProcess.length === 0) {
        return [];
      }

      return lawsToProcess.map(item => createDictionaryFederalLawSelectOption(allLaws!.idDictionary, item));
    },
  );
};

export const getFilteredFederalLawsSelectOptions = makeGetFilteredFederalLawsSelectOptions();

export const getKnmSubjectTypesStatus = (state: IRootState) => state.dictionaries.knmSubjectTypesStatus;

export const getKnmSubjectTypesData = (state: IRootState) => state.dictionaries.knmSubjectTypes;

export const getDictionariesKnmSubjectTypesSelectOptions = createSelector(getKnmSubjectTypesData, dictionaryData => {
  const excludedCodes: string[] = Object.values(KnmSubjectTypeCodesEnum);

  return dictionaryData?.values
    .map(item => createDictionaryKnmSubjectTypesSelectOption(dictionaryData.idDictionary, item))
    .filter(item => !excludedCodes.includes(item.code ?? ''));
});

export const getDictionariesKnoSupervisionParentUuidData = (state: IRootState) => {
  const knoVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoVersionId && supervisionVersionId) {
    return getDictionariesKnoSupervisionParentKnoUuid(state, knoVersionId, supervisionVersionId);
  }
};
export const getKnoSupervisionStartDate = (state: IRootState) => {
  const knoVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoVersionId && supervisionVersionId) {
    return getDictionariesKnoSupervisionData(state, knoVersionId, supervisionVersionId)?.controlStartDate;
  }
};
export const getKnoSupervisionStartAvailableUnplannedActivities = (state: IRootState) => {
  const knoVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoVersionId && supervisionVersionId) {
    return getDictionariesKnoSupervisionData(state, knoVersionId, supervisionVersionId)?.availableUnplannedActivities;
  }
};
export const getDictionariesKnoSupervisionStatus = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
) => getDictionariesKnoSupervision(state, knoVersionId, supervisionVersionId)?.status;
export const getDictionariesKnoSupervisionUuidSelector = (state: IRootState) => (
  knoOrganizationId: string | undefined,
  supervisionTypeId: string | undefined,
) => {
  if (knoOrganizationId && supervisionTypeId) {
    return getDictionariesKnoSupervisionUuid(state, knoOrganizationId, supervisionTypeId);
  }
};
export const getKnoSupervisionStartDateSelector = (state: IRootState) => (
  knoOrganizationId: string | undefined,
  supervisionTypeId: string | undefined,
) => {
  if (knoOrganizationId && supervisionTypeId) {
    return (
      getDictionariesKnoSupervisionData(state, knoOrganizationId, supervisionTypeId)?.controlStartDate || undefined
    );
  }
};

// ----
export const getDictionariesEventUuid = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
  kindErvk: string,
) => state.dictionaries.eventUuids[getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, kindErvk])];
export const getDictionariesEventUuidData = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
  kindErvk: string,
) => getDictionariesEventUuid(state, knoVersionId, supervisionVersionId, kindErvk)?.data;
export const getDictionariesEventUuidStatus = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
  kindErvk: string,
) => getDictionariesEventUuid(state, knoVersionId, supervisionVersionId, kindErvk)?.status;

export const getDictionariesEventHasUnplannedActivity = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
  knmKindVersionId: string,
) =>
  state.dictionaries.eventHasUnplannedActivity[
    getDictionaryConstraintUuidKey([knoVersionId, supervisionVersionId, knmKindVersionId])
  ];
export const getDictionariesEventHasUnplannedActivityData = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
  knmKindVersionId: string,
) => getDictionariesEventHasUnplannedActivity(state, knoVersionId, supervisionVersionId, knmKindVersionId)?.data;
export const getDictionariesEventHasUnplannedActivityStatus = (
  state: IRootState,
  knoVersionId: string,
  supervisionVersionId: string,
  knmKindVersionId: string,
) => getDictionariesEventHasUnplannedActivity(state, knoVersionId, supervisionVersionId, knmKindVersionId)?.status;

export const makeGetDictionaryValuesData = (dictionaryName: DictionaryNamesEnum) => (
  state: IRootState,
  constraintUUIDs?: string[],
) => state.dictionaries.itemsValues[getDictionaryKey(dictionaryName, constraintUUIDs)]?.data;
export const makeGetDictionaryValuesStatus = (dictionaryName: DictionaryNamesEnum) => (
  state: IRootState,
  constraintUUIDs?: string[],
) => state.dictionaries.itemsValues[getDictionaryKey(dictionaryName, constraintUUIDs)]?.status;
export const getDictionarySupervisionTypeAllSelectOptions = (
  state: IRootState,
  constraintUUIDs?: string[],
): IDictionarySelectOption<ISupervisionType>[] => {
  const values = makeGetDictionaryValuesData(DictionaryNamesEnum.SUPERVISION_TYPE)(state, constraintUUIDs);
  const dictionaryValues = values?.map(item => ({
    idValue: item.id,
    value: JSON.parse(item.value),
  }));

  return (
    dictionaryValues?.map(item =>
      createDictionarySupervisionTypeExtendedSelectOption(DictionaryUUIDsEnum.SUPERVISION_TYPE, item, false),
    ) || []
  );
};
export const getDictionaryReasonTypeAllSelectOptions = (state: IRootState): IDictionarySelectOption<IKnmErvk>[] => {
  const values = makeGetDictionaryValuesData(DictionaryNamesEnum.ERVK)(state);
  const dictionaryValues = values?.map(item => ({
    idValue: item.id,
    value: JSON.parse(item.value),
  }));

  return dictionaryValues?.map(item => createDictionaryErvkSelectOption(DictionaryUUIDsEnum.ERVK, item)) || [];
};
export const getDictionaryDecisionKindAllSelectOptions = (
  state: IRootState,
): IDictionarySelectOption<IDecisionKind>[] => {
  const values = makeGetDictionaryValuesData(DictionaryNamesEnum.DECISION_KIND)(state);
  const dictionaryValues = values?.map(item => ({
    idValue: item.id,
    value: JSON.parse(item.value),
  }));

  return (
    dictionaryValues?.map(item => createDictionaryDecisionKindSelectOption(DictionaryUUIDsEnum.DECISION_KIND, item)) ||
    []
  );
};
export const getDictionarySubjectTypeAllSelectOptions = (
  state: IRootState,
): IDictionarySelectOption<ISubjectType>[] => {
  const values = makeGetDictionaryValuesData(DictionaryNamesEnum.SUBJECT_TYPE)(state);
  const dictionaryValues = values?.map(item => ({
    idValue: item.id,
    value: JSON.parse(item.value),
  }));

  return (
    dictionaryValues?.map(item => createDictionarySubjectTypeSelectOption(DictionaryUUIDsEnum.SUBJECT_TYPE, item)) || []
  );
};
export const getDictionaryRiskCategoryAllSelectOptions = (
  state: IRootState,
): IDictionarySelectOption<IRiskCategory248>[] => {
  const values = makeGetDictionaryValuesData(DictionaryNamesEnum.RISK_CATEGORY)(state);
  const dictionaryValues = values?.map(item => ({
    idValue: item.id,
    value: JSON.parse(item.value),
  }));

  return (
    dictionaryValues
      ?.map(item => createDictionaryRiskCategorySelectOption(DictionaryUUIDsEnum.RISK_CATEGORY, item))
      // Отбор категорий риска. У них код не начинает с нуля
      .filter(item => !item.code.startsWith('0')) || []
  );
};
export const getDictionaryDangerClassAllSelectOptions = (
  state: IRootState,
): IDictionarySelectOption<IRiskCategory248>[] => {
  const values = makeGetDictionaryValuesData(DictionaryNamesEnum.RISK_CATEGORY)(state);
  const dictionaryValues = values?.map(item => ({
    idValue: item.id,
    value: JSON.parse(item.value),
  }));

  return (
    dictionaryValues
      ?.map(item => createDictionaryRiskCategorySelectOption(DictionaryUUIDsEnum.RISK_CATEGORY, item))
      // Отбор классов опасности. У них код начинает с нуля
      .filter(item => item.code.startsWith('0')) || []
  );
};
export const getDictionaryEventTypeAllSelectOptions = (state: IRootState): IDictionarySelectOption<IEventType>[] => {
  const values = makeGetDictionaryValuesData(DictionaryNamesEnum.EVENT_TYPE)(state);
  const dictionaryValues = values?.map(item => ({
    idValue: item.id,
    value: JSON.parse(item.value),
  }));

  return dictionaryValues?.map(item => createDictionaryEventTypeSelectOption(DictionaryUUIDsEnum.EVENT_TYPE, item));
};

export const getDictionariesItemsListData = (state: IRootState) => state.dictionaries.itemsList.data;
export const getDictionariesItemsListStatus = (state: IRootState) => state.dictionaries.itemsList.status;

export const getDictionariesItemValueData = (
  state: IRootState,
  dictionaryUUID?: DictionaryUUIDsEnum,
  dictionaryVersionUUID?: string,
) => {
  if (dictionaryUUID && dictionaryVersionUUID) {
    return state.dictionaries.itemValue[getDictionariesItemValueKey(dictionaryUUID)][dictionaryVersionUUID]?.data;
  }
};

export const getDictionariesItemValueStatus = (
  state: IRootState,
  dictionaryUUID?: DictionaryUUIDsEnum,
  dictionaryVersionUUID?: string,
) => {
  if (dictionaryUUID && dictionaryVersionUUID) {
    return state.dictionaries.itemValue[getDictionariesItemValueKey(dictionaryUUID)][dictionaryVersionUUID]?.status;
  }
};
export const makeGetDictionariesItemValueSelectOption = () => {
  return createSelector(
    [
      (state: IRootState, dictionaryUUID?: DictionaryUUIDsEnum, dictionaryVersionUUID?: string) =>
        getDictionariesItemValueData(state, dictionaryUUID, dictionaryVersionUUID),
      (state: IRootState, dictionaryUUID: DictionaryUUIDsEnum) => dictionaryUUID,
    ],
    (dictionaryData, dictionaryUUID) => {
      const dictionaryName = dictionaryNameById[dictionaryUUID];

      if (dictionaryData) {
        return createDictionarySelectOptionByNameMap[dictionaryName](dictionaryUUID, dictionaryData);
      }
    },
  );
};

export const getDictionariesKnoOrganizationsData = (state: IRootState) =>
  state.dictionaries.items[DictionaryNamesEnum.KNO_ORGANIZATIONS]?.data;
export const getDictionariesKnoOrganizationsStatus = (state: IRootState) =>
  state.dictionaries.items[DictionaryNamesEnum.KNO_ORGANIZATIONS]?.status;
export const getDictionariesKnoOrganizationsSelectOptions = createSelector(
  getDictionariesKnoOrganizationsData,
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryKnoOrganizationSelectOption(dictionaryData.idDictionary, item)),
);

export const getDictionariesPositions = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  state.dictionaries.items[DictionaryNamesEnum.POSITIONS][
    getDictionaryKey(DictionaryNamesEnum.POSITIONS, [knoSupervisionUuid || ''])
  ];
export const getDictionariesPositionsData = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesPositions(state, knoSupervisionUuid)?.data;
export const getDictionariesPositionsStatus = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesPositions(state, knoSupervisionUuid)?.status;
export const getDictionariesPositionsSelectOptions = createSelector(getDictionariesPositionsData, dictionaryData =>
  dictionaryData?.values?.map(item => createDictionaryPositionSelectOption(dictionaryData.idDictionary, item)),
);
export const makeGetDictionariesPositionsSelectOptions = () => {
  return createSelector(getDictionariesPositionsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryPositionSelectOption(dictionaryData.idDictionary, item)),
  );
};

/**
 * Получение данных о разрешительных документах через id вида контроля из объекта состояния
 */
export const getDictionariesPermitDocs = (state: IRootState, controlKindUuid: string | undefined) =>
  state.dictionaries.items[DictionaryNamesEnum.PERMIT_DOCS][
    getDictionaryKey(DictionaryNamesEnum.PERMIT_DOCS, [controlKindUuid || ''])
  ];
export const getDictionariesPermitDocsData = (state: IRootState, controlKindUuid: string | undefined) =>
  getDictionariesPermitDocs(state, controlKindUuid)?.data;
export const getDictionariesPermitDocsStatus = (state: IRootState, controlKindUuid: string | undefined) =>
  getDictionariesPermitDocs(state, controlKindUuid)?.status;

/**
 * Генерация списка опций для селекта разрешительных документов
 */
export const makeGetDictionariesPermitDocsSelectOptions = () => {
  return createSelector(getDictionariesPermitDocsData, dictionaryData => {
    return dictionaryData?.map(item => {
      const dictionaryValue: IDictionaryValue<IPermitDocs> = {
        idValue: item.targetId,
        value: item,
      };

      return createDictionaryPermitDocsSelectOption(DictionaryUUIDsEnum.PERMIT_DOCS, dictionaryValue);
    });
  });
};

export const getDictionariesParticipantPositions = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  state.dictionaries.items[DictionaryNamesEnum.PARTICIPANT_POSITIONS][
    getDictionaryKey(DictionaryNamesEnum.PARTICIPANT_POSITIONS, [knoSupervisionUuid || ''])
  ];
export const getDictionariesParticipantPositionsData = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesParticipantPositions(state, knoSupervisionUuid)?.data;
export const getDictionariesParticipantPositionsStatus = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesParticipantPositions(state, knoSupervisionUuid)?.status;
export const getDictionariesParticipantPositionsSelectOptions = createSelector(
  getDictionariesParticipantPositionsData,
  dictionaryData =>
    dictionaryData?.values?.map(item =>
      createDictionaryParticipantPositionSelectOption(dictionaryData.idDictionary, item),
    ),
);
export const makeGetDictionariesParticipantPositionsSelectOptions = () => {
  return createSelector(getDictionariesParticipantPositionsData, dictionaryData =>
    dictionaryData?.values?.map(item =>
      createDictionaryParticipantPositionSelectOption(dictionaryData.idDictionary, item),
    ),
  );
};

export const getDictionariesKnmKinds = (state: IRootState, triplet: ITripletKnoSupervisionCondition | undefined) => {
  // @ts-ignore
  const filter: string[] = triplet?.conditionUuid
    ? [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.conditionUuid, triplet?.orderVersionId]
    : [triplet?.knoVersionId, triplet?.supervisionVersionId, triplet?.orderVersionId];

  return state.dictionaries.items[DictionaryNamesEnum.KNM_KIND][
    getDictionaryKey(DictionaryNamesEnum.KNM_KIND, triplet ? filter : undefined)
  ];
};

export const getDictionariesKnmKindsData = (state: IRootState, triplet: ITripletKnoSupervisionCondition | undefined) =>
  getDictionariesKnmKinds(state, triplet)?.data;
export const getDictionariesKnmKindsStatus = (
  state: IRootState,
  triplet: ITripletKnoSupervisionCondition | undefined,
) => getDictionariesKnmKinds(state, triplet)?.status;
export const makeGetDictionariesKnmKindsSelectOptions = () => {
  return createSelector(getDictionariesKnmKindsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryKnmKindSelectOption(dictionaryData.idDictionary, item)),
  );
};
export const getDictionariesKnmKindsSelectOptionsSelector = (state: IRootState) => (
  triplet: ITripletKnoSupervisionCondition | undefined,
) => {
  const dictionaryData = getDictionariesKnmKindsData(state, triplet);

  return dictionaryData?.values?.map(item => createDictionaryKnmKindSelectOption(dictionaryData.idDictionary, item));
};
export const getDictionariesAllKnmKindsSelectOptions = createSelector(
  (state: IRootState) => getDictionariesKnmKindsData(state, undefined),
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryKnmKindSelectOption(dictionaryData.idDictionary, item)),
);
export const getDictionariesAllProfKindsSelectOptions = createSelector(
  (state: IRootState) => getDictionariesProfKindsData(state, undefined, undefined),
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryProfKindSelectOption(dictionaryData.idDictionary, item)),
);

export const getDictionariesDecisionKinds = (state: IRootState, pair: IPairKnoSupervisionVersions | undefined) => {
  // @ts-ignore
  const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId, pair?.orderVersionId];

  return state.dictionaries.items[DictionaryNamesEnum.DECISION_KIND][
    getDictionaryKey(DictionaryNamesEnum.DECISION_KIND, pair ? filter : undefined)
  ];
};

export const getDictionariesDecisionKindsData = (state: IRootState, pair: IPairKnoSupervisionVersions | undefined) =>
  getDictionariesDecisionKinds(state, pair)?.data;
export const getDictionariesDecisionKindsStatus = (state: IRootState, pair: IPairKnoSupervisionVersions | undefined) =>
  getDictionariesDecisionKinds(state, pair)?.status;
export const makeGetDictionariesDecisionKindsSelectOptions = () => {
  return createSelector(getDictionariesDecisionKindsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryDecisionKindSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesDecisionKindsAll = (state: IRootState, pair: IPairKnoSupervisionVersions | undefined) => {
  // @ts-ignore
  const filter: string[] = [pair?.knoVersionId, pair?.supervisionVersionId, pair?.orderVersionId];

  return state.dictionaries.items[DictionaryNamesEnum.DECISION_KIND_ALL][
    getDictionaryKey(DictionaryNamesEnum.DECISION_KIND_ALL, pair ? filter : undefined)
  ];
};

export const getDictionariesDecisionKindsAllData = (state: IRootState, pair: IPairKnoSupervisionVersions | undefined) =>
  getDictionariesDecisionKindsAll(state, pair)?.data;
export const getDictionariesDecisionKindsAllStatus = (
  state: IRootState,
  pair: IPairKnoSupervisionVersions | undefined,
) => getDictionariesDecisionKindsAll(state, pair)?.status;
export const makeGetDictionariesDecisionKindsAllSelectOptions = () => {
  return createSelector(getDictionariesDecisionKindsAllData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryDecisionKindSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesDecisionKindsAllSelectOptions = createSelector(
  (state: IRootState) => getDictionariesDecisionKindsAllData(state, undefined),
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryDecisionKindSelectOption(dictionaryData.idDictionary, item)),
);

export const getDictionariesProfKinds = (
  state: IRootState,
  pair: IPairKnoSupervisionVersions | undefined,
  conditionUuid: string | undefined,
) => {
  const filter: string[] = [
    pair?.knoVersionId || '',
    pair?.supervisionVersionId || '',
    pair?.orderVersionId || '',
    conditionUuid || '',
  ];

  return state.dictionaries.items[DictionaryNamesEnum.PROF_KIND][
    getDictionaryKey(DictionaryNamesEnum.PROF_KIND, pair ? filter : undefined)
  ];
};

export const getDictionariesProfKindsData = (
  state: IRootState,
  pair: IPairKnoSupervisionVersions | undefined,
  conditionUuid: string | undefined,
) => getDictionariesProfKinds(state, pair, conditionUuid)?.data;
export const getDictionariesProfKindsStatus = (
  state: IRootState,
  pair: IPairKnoSupervisionVersions | undefined,
  conditionUuid: string | undefined,
) => getDictionariesProfKinds(state, pair, conditionUuid)?.status;
export const makeGetDictionariesProfKindsSelectOptions = () => {
  return createSelector(getDictionariesProfKindsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryProfKindSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesAcceptedDocuments = (state: IRootState, pair: IPairKnoSupervisionVersions | undefined) =>
  state.dictionaries.items[DictionaryNamesEnum.ACCEPTED_DOCUMENT][
    getDictionaryKey(
      DictionaryNamesEnum.ACCEPTED_DOCUMENT,
      pair ? [pair.knoVersionId, pair.supervisionVersionId] : undefined,
    )
  ];
export const getDictionariesAcceptedDocumentsData = (
  state: IRootState,
  pair: IPairKnoSupervisionVersions | undefined,
) => getDictionariesAcceptedDocuments(state, pair)?.data;
export const getDictionariesAcceptedDocumentsStatus = (
  state: IRootState,
  pair: IPairKnoSupervisionVersions | undefined,
) => getDictionariesAcceptedDocuments(state, pair)?.status;

export const makeGetDictionariesAcceptedDocumentsSelectOptions = () => {
  return createSelector(getDictionariesAcceptedDocumentsData, dictionaryData =>
    dictionaryData?.values?.map(item =>
      createDictionaryAcceptedDocumentSelectOption(dictionaryData.idDictionary, item),
    ),
  );
};

export const getDictionariesRiskCategories = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId: string | undefined,
  knmKindUuid: string | undefined,
  isPlanned: boolean | undefined,
) => {
  const isPlannedValue = typeof isPlanned === 'boolean' ? String(isPlanned) : '';

  return state.dictionaries.items[DictionaryNamesEnum.RISK_CATEGORY][
    getDictionaryKey(DictionaryNamesEnum.RISK_CATEGORY, [
      knoSupervisionUuid || '',
      conditionUuid || '',
      orderVersionId || '',
      knmKindUuid || '',
      isPlannedValue,
    ])
  ];
};
export const getDictionariesRiskCategoriesData = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId: string | undefined,
  knmKindUuid: string | undefined,
  isPlanned: boolean | undefined,
) =>
  getDictionariesRiskCategories(state, knoSupervisionUuid, conditionUuid, orderVersionId, knmKindUuid, isPlanned)?.data;
export const getDictionariesRiskCategoriesStatus = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId: string | undefined,
  knmKindUuid: string | undefined,
  isPlanned: boolean | undefined,
) =>
  getDictionariesRiskCategories(state, knoSupervisionUuid, conditionUuid, orderVersionId, knmKindUuid, isPlanned)
    ?.status;

export const getDictionariesRiskCategoriesSelectOptions = createSelector(
  getDictionariesRiskCategoriesData,
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryRiskCategorySelectOption(dictionaryData.idDictionary, item)),
);
export const makeGetDictionariesRiskCategoriesSelectOptions = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId: string | undefined,
  knmKindUuid: string | undefined,
  isPlanned: boolean | undefined,
) => {
  const dictionaryData = getDictionariesRiskCategoriesData(
    state,
    knoSupervisionUuid,
    conditionUuid,
    orderVersionId,
    knmKindUuid,
    isPlanned,
  );

  return dictionaryData
    ? dictionaryData.values?.map(item => createDictionaryRiskCategorySelectOption(dictionaryData.idDictionary, item))
    : [];
};

export const getDictionariesDangerClassesData = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId: string | undefined,
  isPlanned: boolean | undefined,
) =>
  state.dictionaries.items[DictionaryNamesEnum.DANGER_CLASS][
    getDictionaryKey(DictionaryNamesEnum.DANGER_CLASS, [
      knoSupervisionUuid || '',
      conditionUuid || '',
      orderVersionId || '',
      typeof isPlanned === 'boolean' ? String(isPlanned) : '',
    ])
  ]?.data;
export const getDictionariesDangerClassesStatus = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId: string | undefined,
  isPlanned: boolean | undefined,
) =>
  state.dictionaries.items[DictionaryNamesEnum.DANGER_CLASS][
    getDictionaryKey(DictionaryNamesEnum.DANGER_CLASS, [
      knoSupervisionUuid || '',
      conditionUuid || '',
      orderVersionId || '',
      typeof isPlanned === 'boolean' ? String(isPlanned) : '',
    ])
  ]?.status;

export const getDictionariesDangerClassesSelectOptions = createSelector(
  getDictionariesDangerClassesData,
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryDangerClassSelectOption(dictionaryData.idDictionary, item)),
);
export const makeGetDictionariesDangerClassesSelectOptions = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId: string | undefined,
  isPlanned: boolean | undefined,
) => {
  const dictionaryData = getDictionariesDangerClassesData(
    state,
    knoSupervisionUuid,
    conditionUuid,
    orderVersionId,
    isPlanned,
  );

  return dictionaryData
    ? dictionaryData.values?.map(item => createDictionaryDangerClassSelectOption(dictionaryData.idDictionary, item))
    : [];
};

export const getDictionariesSubjectTypes = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) =>
  state.dictionaries.items[DictionaryNamesEnum.SUBJECT_TYPE][
    getDictionaryKey(DictionaryNamesEnum.SUBJECT_TYPE, [
      knoSupervisionUuid || '',
      conditionUuid || '',
      orderVersionId || '',
    ])
  ];
export const getDictionariesSubjectTypesData = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) => getDictionariesSubjectTypes(state, knoSupervisionUuid, conditionUuid, orderVersionId)?.data;
export const getDictionariesSubjectTypesStatus = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) => getDictionariesSubjectTypes(state, knoSupervisionUuid, conditionUuid, orderVersionId)?.status;
export const getDictionariesSubjectTypesSelectOptions = createSelector(
  getDictionariesSubjectTypesData,
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionarySubjectTypeSelectOption(dictionaryData.idDictionary, item)),
);
export const makeGetDictionariesSubjectTypesSelectOptions = () => {
  return createSelector(getDictionariesSubjectTypesData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionarySubjectTypeSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesSubjectKinds = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
  objectTypeDictVersionId?: string,
) =>
  state.dictionaries.items[DictionaryNamesEnum.SUBJECT_KIND][
    getDictionaryKey(DictionaryNamesEnum.SUBJECT_KIND, [
      knoSupervisionUuid || '',
      conditionUuid || '',
      orderVersionId || '',
      objectTypeDictVersionId || '',
    ])
  ];
export const getDictionariesSubjectKindsData = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string | undefined,
  objectTypeDictVersionId?: string,
) =>
  getDictionariesSubjectKinds(state, knoSupervisionUuid, conditionUuid, orderVersionId, objectTypeDictVersionId)?.data;
export const getDictionariesSubjectKindsStatus = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
  objectTypeDictVersionId?: string,
) =>
  getDictionariesSubjectKinds(state, knoSupervisionUuid, conditionUuid, orderVersionId, objectTypeDictVersionId)
    ?.status;
export const getDictionariesSubjectKindsSelectOptions = createSelector(
  getDictionariesSubjectKindsData,
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionarySubjectKindSelectOption(dictionaryData.idDictionary, item)),
);
export const makeGetDictionariesSubjectKindsSelectOptions = () => {
  return createSelector(getDictionariesSubjectKindsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionarySubjectKindSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesSubjectSubKinds = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
  objectTypeDictVersionId?: string,
) =>
  state.dictionaries.items[DictionaryNamesEnum.SUBJECT_SUB_KIND][
    getDictionaryKey(DictionaryNamesEnum.SUBJECT_SUB_KIND, [
      knoSupervisionUuid || '',
      conditionUuid || '',
      orderVersionId || '',
      objectTypeDictVersionId || '',
    ])
  ];
export const getDictionariesSubjectSubKindsData = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
  objectTypeDictVersionId?: string,
) =>
  getDictionariesSubjectSubKinds(state, knoSupervisionUuid, conditionUuid, orderVersionId, objectTypeDictVersionId)
    ?.data;
export const getDictionariesSubjectSubKindsStatus = (
  state: IRootState,
  knoSupervisionUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
  objectTypeDictVersionId?: string,
) =>
  getDictionariesSubjectSubKinds(state, knoSupervisionUuid, conditionUuid, orderVersionId, objectTypeDictVersionId)
    ?.status;

export const getDictionariesSubjectSubKindsSelectOptions = createSelector(
  getDictionariesSubjectSubKindsData,
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionarySubjectSubKindSelectOption(dictionaryData.idDictionary, item)),
);
export const makeGetDictionariesSubjectSubKindsSelectOptions = () => {
  return createSelector(getDictionariesSubjectSubKindsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionarySubjectSubKindSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesEventTypes = (
  state: IRootState,
  eventUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) =>
  state.dictionaries.items[DictionaryNamesEnum.EVENT_TYPE][
    getDictionaryKey(DictionaryNamesEnum.EVENT_TYPE, [eventUuid || '', conditionUuid || '', orderVersionId || ''])
  ];
export const getDictionariesEventTypesData = (
  state: IRootState,
  eventUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) => getDictionariesEventTypes(state, eventUuid, conditionUuid, orderVersionId)?.data;
export const getDictionariesEventTypesStatus = (
  state: IRootState,
  eventUuid: string | undefined,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) => getDictionariesEventTypes(state, eventUuid, conditionUuid, orderVersionId)?.status;
export const makeGetDictionariesEventTypesSelectOptions = () => {
  return createSelector(getDictionariesEventTypesData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryEventTypeSelectOption(dictionaryData.idDictionary, item)),
  );
};
export const getDictionariesAllEventTypesSelectOptions = createSelector(
  (state: IRootState) => getDictionariesEventTypesData(state, undefined, undefined),
  dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryEventTypeSelectOption(dictionaryData.idDictionary, item)),
);
// getKnmFormRef(state)
export const getDictionariesPmProfEventTypes = (state: IRootState, isUnableToRefuse?: boolean) => {
  return state.dictionaries.items[DictionaryNamesEnum.PM_PROF_EVENT_TYPE][
    getDictionaryKey(DictionaryNamesEnum.PM_PROF_EVENT_TYPE, [isUnableToRefuse?.toString() || 'false'])
  ];
};
export const getDictionariesPmProfEventTypesData = (state: IRootState, isUnableToRefuse: boolean = false) =>
  getDictionariesPmProfEventTypes(state, isUnableToRefuse)?.data;
export const getDictionariesPmProfEventTypesStatus = (state: IRootState, isUnableToRefuse: boolean = false) =>
  getDictionariesPmProfEventTypes(state, isUnableToRefuse)?.status;
export const makeGetDictionariesPmProfEventTypesSelectOptions = () => {
  return createSelector(
    (state: IRootState) => {
      const isUnableToRefuse = getKnmPmIsUnableToRefuseFormValue(state);

      return getDictionariesPmProfEventTypesData(state, isUnableToRefuse);
    },
    dictionaryData =>
      dictionaryData?.values?.map(item => createDictionaryEventTypeSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesSupervisionTypesData = (
  state: IRootState,
  constraintUUIDs: string[],
  isPlanned: boolean,
  federalLawUUIDs?: string[],
) => {
  return state.dictionaries.items[DictionaryNamesEnum.SUPERVISION_TYPE][
    getDictionaryKey(DictionaryNamesEnum.SUPERVISION_TYPE, [
      ...constraintUUIDs,
      isPlanned.toString(),
      ...(federalLawUUIDs || ''),
    ])
  ]?.data;
};
export const getDictionariesSupervisionTypesStatus = (
  state: IRootState,
  constraintUUIDs: string[],
  isPlanned: boolean,
  federalLawUUIDs?: string[],
) => {
  return state.dictionaries.items[DictionaryNamesEnum.SUPERVISION_TYPE][
    getDictionaryKey(DictionaryNamesEnum.SUPERVISION_TYPE, [
      ...constraintUUIDs,
      isPlanned.toString(),
      ...(federalLawUUIDs || ''),
    ])
  ]?.status;
};

export const getDictionariesSupervisionTypesBySubjectTypeData = (state: IRootState) => {
  return state.dictionaries.items[DictionaryNamesEnum.SUPERVISION_TYPE_BY_SUBJECT_TYPE][
    getDictionaryKey(DictionaryNamesEnum.SUPERVISION_TYPE_BY_SUBJECT_TYPE)
  ]?.data;
};

export const makeGetDictionariesSupervisionTypesSelectOptions = () => (
  state: IRootState,
  constraintUUIDs: string[],
) => {
  const isPlanned = getKnmIsTypeErknmPlanned(state) || !!getKnmDataPlanId(state);
  const dictionaryData = getDictionariesSupervisionTypesData(state, constraintUUIDs, isPlanned);

  return dictionaryData?.values?.map(item =>
    createDictionarySupervisionTypeSelectOption(dictionaryData.idDictionary, item),
  );
};

export const makeGetDictionariesSupervisionTypesBySubjectTypeSelectOptions = () => (state: IRootState) => {
  const dictionaryData = getDictionariesSupervisionTypesBySubjectTypeData(state);

  return dictionaryData?.values?.map(item =>
    createDictionarySupervisionTypeSelectOption(dictionaryData.idDictionary, item),
  );
};

export const makeGetDictionariesSupervisionTypesExtendedSelectOptions = () => (
  state: IRootState,
  constraintUUIDs: string[],
  isUseTransferringAuthorities: boolean,
  isPlanned?: boolean,
  federalLawUUIDs?: string[],
) => {
  const dictionaryData = getDictionariesSupervisionTypesData(state, constraintUUIDs, !!isPlanned, federalLawUUIDs);

  return dictionaryData?.values?.map(item =>
    createDictionarySupervisionTypeExtendedSelectOption(
      dictionaryData.idDictionary,
      item,
      isUseTransferringAuthorities,
    ),
  );
};

export const getDictionariesSupervisionTypesSelectOptionsSelector = (state: IRootState) => (
  knoOrganizationId: string | undefined,
  isUseTransferringAuthorities: boolean,
  federalLawUUIDs: string[],
  isPlanned?: boolean,
) => {
  const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();

  if (knoOrganizationId) {
    return getDictionariesSupervisionTypesSelectOptions(
      state,
      [knoOrganizationId],
      isUseTransferringAuthorities,
      isPlanned,
      federalLawUUIDs,
    );
  }

  return undefined;
};

export const getDictionariesChecklistsData = (state: IRootState, filterParams: IGetDictionariesChecklistsParams) =>
  state.dictionaries.items[DictionaryNamesEnum.CHECKLIST][
    getDictionaryKey(DictionaryNamesEnum.CHECKLIST, [filterParams.uuidKno, filterParams.uuidSupervision])
  ]?.data;
export const getDictionariesChecklistsStatus = (state: IRootState, filterParams: IGetDictionariesChecklistsParams) =>
  state.dictionaries.items[DictionaryNamesEnum.CHECKLIST][
    getDictionaryKey(DictionaryNamesEnum.CHECKLIST, [filterParams.uuidKno, filterParams.uuidSupervision])
  ]?.status;

export const makeGetDictionariesChecklistsSelectOptions = () => {
  return createSelector(getDictionariesChecklistsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryChecklistSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getErvkChecklistsData = (state: IRootState, filterParams: IGetErvkCheckListParams) => {
  return state.dictionaries.items[DictionaryNamesEnum.ERVK_CHECK_LIST][
    getDictionaryKey(DictionaryNamesEnum.ERVK_CHECK_LIST, [filterParams.controlKindId, filterParams.knmKindId])
  ]?.data;
};

export const makeGetErvkChecklistsSelectOptions = () => {
  return createSelector(getErvkChecklistsData, dictionaryData =>
    dictionaryData?.map(item => {
      const dictionaryValue: IDictionaryValue<IErvkCheckList> = {
        idValue: item.id || '',
        value: item,
      };

      return createErvkChecklistSelectOption(DictionaryUUIDsEnum.ERVK_CHECK_LIST, dictionaryValue);
    }),
  );
};

export const getDictionariesRiskIndicators = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  state.dictionaries.items[DictionaryNamesEnum.RISK_INDICATORY][
    getDictionaryKey(DictionaryNamesEnum.RISK_INDICATORY, [knoSupervisionUuid || ''])
  ];
export const getDictionariesRiskIndicatorsData = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesRiskIndicators(state, knoSupervisionUuid)?.data;
export const getDictionariesRiskIndicatorsStatus = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesRiskIndicators(state, knoSupervisionUuid)?.status;
export const makeGetDictionariesRiskIndicatorsSelectOptions = () => {
  return createSelector(getDictionariesRiskIndicatorsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryRiskIndicatorySelectOption(dictionaryData.idDictionary, item)),
  );
};

export const makeGetDictionariesRiskIndicatorsSelectOptionsForDecision = () => {
  return createSelector(getDictionariesRiskIndicatorsData, dictionaryData =>
    dictionaryData?.values?.map(item =>
      createDictionaryRiskIndicatorySelectOptionForDecision(dictionaryData.idDictionary, item),
    ),
  );
};

export const getDictionariesRequirementsData = (state: IRootState, filterParams: IGetDictionariesRequirementsParams) =>
  state.dictionaries.items[DictionaryNamesEnum.REQUIREMENT][
    getDictionaryKey(DictionaryNamesEnum.REQUIREMENT, [filterParams.uuidKno, filterParams.uuidSupervision])
  ]?.data;
export const getDictionariesRequirementsStatus = (
  state: IRootState,
  filterParams: IGetDictionariesRequirementsParams,
) =>
  state.dictionaries.items[DictionaryNamesEnum.REQUIREMENT][
    getDictionaryKey(DictionaryNamesEnum.REQUIREMENT, [filterParams.uuidKno, filterParams.uuidSupervision])
  ]?.status;
export const getErvkCheckListStatus = (state: IRootState, filterParams: IGetErvkCheckListParams) =>
  state.dictionaries.items[DictionaryNamesEnum.REQUIREMENT][
    getDictionaryKey(DictionaryNamesEnum.REQUIREMENT, [filterParams.controlKindId, filterParams.knmKindId])
  ]?.status;
export const makeGetDictionariesRequirementsSelectOptions = () => {
  return createSelector(getDictionariesRequirementsData, dictionaryData =>
    dictionaryData?.values?.map(item => createDictionaryRequirementSelectOption(dictionaryData.idDictionary, item)),
  );
};

export const getDictionariesTargetEntity = (
  state: IRootState,
  triplet?: ITripletKnoSupervisionCondition | undefined,
) => {
  if (triplet) {
    const filter: string[] = triplet.conditionUuid
      ? [triplet.knoVersionId, triplet.supervisionVersionId, triplet.conditionUuid, triplet?.orderVersionId || '']
      : [triplet.knoVersionId, triplet.supervisionVersionId, triplet?.orderVersionId || ''];

    return state.dictionaries.items[DictionaryNamesEnum.TARGET_ENTITY][
      getDictionaryKey(DictionaryNamesEnum.TARGET_ENTITY, filter)
    ];
  }

  return state.dictionaries.items[DictionaryNamesEnum.TARGET_ENTITY][
    getDictionaryKey(DictionaryNamesEnum.TARGET_ENTITY, undefined)
  ];
};
export const getDictionariesTargetEntityData = (
  state: IRootState,
  triplet: ITripletKnoSupervisionCondition | undefined,
) => getDictionariesTargetEntity(state, triplet)?.data;
export const getDictionariesTargetEntityStatus = (
  state: IRootState,
  triplet: ITripletKnoSupervisionCondition | undefined,
) => getDictionariesTargetEntity(state, triplet)?.status;

export const getDictionariesTargetEntityFounder = (state: IRootState) => {
  return state.dictionaries.items[DictionaryNamesEnum.TARGET_ENTITY_FOUNDER][
    getDictionaryKey(DictionaryNamesEnum.TARGET_ENTITY_FOUNDER, undefined)
  ];
};
export const getDictionariesTargetEntityFounderData = (state: IRootState) =>
  getDictionariesTargetEntityFounder(state)?.data;
export const getDictionariesTargetEntityFounderStatus = (state: IRootState) =>
  getDictionariesTargetEntityFounder(state)?.status;

export const getDictionariesSubjectTypesMulti = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => {
  const filter: string[] = [...knoVersionIds, ...supervisionVersionIds]; // TODO локализовать с reducer'ом

  return state.dictionaries.items[DictionaryNamesEnum.SUBJECT_TYPE][
    getDictionaryKey(DictionaryNamesEnum.SUBJECT_TYPE, filter)
  ];
};
export const getDictionariesSubjectTypesMultiData = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => getDictionariesSubjectTypesMulti(state, knoVersionIds, supervisionVersionIds)?.data;
export const getDictionariesSubjectTypesMultiStatus = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => getDictionariesSubjectTypesMulti(state, knoVersionIds, supervisionVersionIds)?.status;

export const getDictionariesSubjectKindsMulti = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => {
  const filter: string[] = [...knoVersionIds, ...supervisionVersionIds]; // TODO локализовать с reducer'ом

  return state.dictionaries.items[DictionaryNamesEnum.SUBJECT_KIND][
    getDictionaryKey(DictionaryNamesEnum.SUBJECT_KIND, filter)
  ];
};
export const getDictionariesSubjectKindsMultiData = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => getDictionariesSubjectKindsMulti(state, knoVersionIds, supervisionVersionIds)?.data;
export const getDictionariesSubjectKindsMultiStatus = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => getDictionariesSubjectKindsMulti(state, knoVersionIds, supervisionVersionIds)?.status;

export const getDictionariesSubjectSubKindsMulti = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => {
  const filter: string[] = [...knoVersionIds, ...supervisionVersionIds]; // TODO локализовать с reducer'ом

  return state.dictionaries.items[DictionaryNamesEnum.SUBJECT_SUB_KIND][
    getDictionaryKey(DictionaryNamesEnum.SUBJECT_SUB_KIND, filter)
  ];
};
export const getDictionariesSubjectSubKindsMultiData = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => getDictionariesSubjectSubKindsMulti(state, knoVersionIds, supervisionVersionIds)?.data;
export const getDictionariesSubjectSubKindsMultiStatus = (
  state: IRootState,
  knoVersionIds: string[],
  supervisionVersionIds: string[],
) => getDictionariesSubjectSubKindsMulti(state, knoVersionIds, supervisionVersionIds)?.status;

export const makeGetDictionariesTargetEntitySelectOptions = () => {
  return createSelector(getDictionariesTargetEntityData, targetEntity =>
    targetEntity?.values?.map(item => createDictionaryTargetEntitySelectOption(targetEntity.idDictionary, item)),
  );
};

export const makeGetDictionariesTargetEntityFounderSelectOptions = () => {
  return createSelector(getDictionariesTargetEntityFounderData, targetEntity =>
    targetEntity?.values?.map(item => createDictionaryTargetEntitySelectOption(targetEntity.idDictionary, item)),
  );
};

export const makeGetDictionariesSubjectTypesMultiSelectOptions = () => {
  return createSelector(getDictionariesSubjectTypesMultiData, subjectType =>
    subjectType?.values?.map(item => createDictionarySubjectTypeSelectOption(subjectType.idDictionary, item)),
  );
};

export const makeGetDictionariesSubjectKindsMultiSelectOptions = () => {
  return createSelector(getDictionariesSubjectKindsMultiData, subjectType =>
    subjectType?.values?.map(item => createDictionarySubjectKindSelectOption(subjectType.idDictionary, item)),
  );
};

export const makeGetDictionariesSubjectSubKindsMultiSelectOptions = () => {
  return createSelector(getDictionariesSubjectSubKindsMultiData, subjectType =>
    subjectType?.values?.map(item => createDictionarySubjectKindSelectOption(subjectType.idDictionary, item)),
  );
};

export const getDictionariesKnmTypes = (state: IRootState, knoSupervisionUuid?: string | undefined) => {
  const knoSupervisionUuidState = getKnmKnoSupervisionUuid(state);

  return state.dictionaries.items[DictionaryNamesEnum.KNM_TYPES][
    getDictionaryKey(DictionaryNamesEnum.KNM_TYPES, [knoSupervisionUuid || knoSupervisionUuidState || ''])
  ];
};
export const getDictionariesKnmTypesData = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesKnmTypes(state, knoSupervisionUuid)?.data;
export const getDictionariesKnmTypesStatus = (state: IRootState, knoSupervisionUuid: string | undefined) =>
  getDictionariesKnmTypes(state, knoSupervisionUuid)?.status;
export const getKnmKindDictionaryItemValue = (state: IRootState, itemId: string) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getDictionariesKnmKindsData(state, {
      knoVersionId: knoOrganizationDictVersionId,
      supervisionVersionId: supervisionTypeDictVersionId,
    })?.values?.find(x => x.idValue === itemId);
  }

  return undefined;
};

export const getDictionariesConditionTypes = (state: IRootState, uniqueUuid: string | undefined) =>
  state.dictionaries.items[DictionaryNamesEnum.CONDITION_TYPES][
    getDictionaryKey(DictionaryNamesEnum.CONDITION_TYPES, [uniqueUuid || ''])
  ];
export const getDictionariesConditionTypesData = (state: IRootState, uniqueUuid: string | undefined) =>
  getDictionariesConditionTypes(state, uniqueUuid)?.data;
export const getDictionariesConditionTypesStatus = (state: IRootState, uniqueUuid: string | undefined) =>
  getDictionariesConditionTypes(state, uniqueUuid)?.status;

export const getDictionariesApplicationConditionSubjects = (
  state: IRootState,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) =>
  state.dictionaries.items[DictionaryNamesEnum.APPLICATION_CONDITION_SUBJECTS][
    getDictionaryKey(DictionaryNamesEnum.APPLICATION_CONDITION_SUBJECTS, [orderVersionId || conditionUuid || ''])
  ];
export const getDictionariesApplicationConditionSubjectsData = (
  state: IRootState,
  conditionUuid: string | undefined,
  orderVersionId?: string,
) => getDictionariesApplicationConditionSubjects(state, conditionUuid, orderVersionId)?.data;
export const getDictionariesApplicationConditionSubjectsStatus = (
  state: IRootState,
  conditionUuid?: string,
  orderVersionId?: string,
) => getDictionariesApplicationConditionSubjects(state, conditionUuid, orderVersionId)?.status;

export const hasAnyDictionaryFailed = (state: IRootState) => {
  if (getLawInterface(state) !== LawInterfacesEnum.FZ_248) {
    return false;
  }

  return hasDictionaryFailedDeep(getDictionariesState(state));
};

const normalizeFederalLawNumber = (value?: string | null) => value?.replace(/\D/g, '') ?? '';

export const getPlanTypeWithFederalLawSelectOptions = (
  state: IRootState,
): IDictionarySelectOption<IPlanTypeWithFederalLaw>[] | undefined => {
  const federalLaws = getFilteredFederalLawsSelectOptions(state);
  const planTypes = getKnmPlanTypeSelectOptions(state) as IKnmPlanTypeOption[] | undefined;

  if (!federalLaws || !planTypes) {
    return undefined;
  }

  return federalLaws.flatMap(law => {
    const lawNumber = normalizeFederalLawNumber(law.number);
    const planType = planTypes.find(type => normalizeFederalLawNumber(type.number) === lawNumber);

    return planType
      ? [
          {
            ...law,
            planTypeId: String(planType.id),
            label: planType.label,
          },
        ]
      : [];
  });
};

export const getPlanTypeFilterSelectOptions = (state: IRootState): ISelectOption[] | undefined => {
  const options = getPlanTypeWithFederalLawSelectOptions(state);

  if (!options) {
    return undefined;
  }

  return options.map(option => ({
    id: option.planTypeId,
    value: option.planTypeId,
    label: option.label,
  }));
};
